import { Box, Grid } from "@mui/material";
import { DateRangePicker, DateTimeRangePicker } from "@mui/x-date-pickers-pro";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import WorkLocationAutocomplete from "../../../common/components/functional/autocomplete/workLocationAutocomplete/WorkLocationAutocomplete";
import useWorkLocationAutocomplete from "../../../common/components/functional/autocomplete/workLocationAutocomplete/useWorkLocationAutocomplete";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { SearchFilter } from "../../../common/components/functional/FilterSearchField/FilterSearchField";
import { CenteredGridItem } from "../../../common/components/functional/FilterSearchField/styled";
import { RoundedButton } from "../../../common/components/styled/styled";
import { trueFalseAutocompleteOptions } from "../../../common/helpers/common";
import {
  convertBooleanToYesNoValue,
  dateToFormatOrEmpty,
  dateToTimeOrEmpty,
  makeLookupValues,
  makeMultiSelectDisplay,
} from "../../../common/helpers/filters";
import {
  dateTimeFormat,
  isoFormat,
  leadingZerosDateFormat,
} from "../../../common/helpers/formatDateTime";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { CustomerOrganization } from "../../customer/customerAPI";
import { WorkLocation } from "../../customer/types/queries";
import { FilterRow } from "../../invoices/filters/FilterRow";
import { fetchOrderQueues } from "../../settings/settingsActions";
import {
  fetchExpertUsers,
  selectExpertUserOptions,
} from "../../user/expertUsersSlice";
import {
  FILTER_ORDER_STATUS,
  FILTER_ORDER_TYPES,
  FILTER_ORDER_URGENCY,
  FILTER_QUEUE_OWN,
} from "../constants";

import { SelectAutocompleteMulti } from "../../../common/components/functional/SelectAutocomplete/SelectAutocompleteMulti";
import { SelectAutocompleteSingle } from "../../../common/components/functional/SelectAutocomplete/SelectAutocompleteSingle";
import CustomerOrgCompanyAutocompleteMulti from "../../../common/components/functional/autocomplete/customerOrgCompanyAutocomplete/CustomerOrgCompanyAutocompleteMulti";
import useCustomerOrgCompanyAutocomplete from "../../../common/components/functional/autocomplete/customerOrgCompanyAutocomplete/useCustomerOrgCompanyAutocomplete";
import {
  DEFAULT_ORDER_FORM,
  OrderFilterFormTypes,
  parseOrderFilters,
} from "./common";

interface Props {
  filters: SearchFilter[];
  updateFilters: (filters: any) => void;
  handleMenuToggle: () => void;
}

export const OrderFilters = ({
  filters,
  updateFilters,
  handleMenuToggle,
}: Props) => {
  const { t } = useTranslation(["order", "common"]);
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.user.user);
  const expertUserOptions = useAppSelector(selectExpertUserOptions);
  const expertOptions = useMemo(
    () => expertUserOptions.filter((expert) => expert.id !== user?.id),
    [expertUserOptions, user?.id],
  );

  const { control, handleSubmit, reset } = useForm<OrderFilterFormTypes>({
    defaultValues: DEFAULT_ORDER_FORM,
  });

  const [orderQueueOptions, setOrderQueueOptions] = useState<
    AutocompleteType[]
  >([]);

  useEffect(() => {
    reset(parseOrderFilters(filters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const {
    workLocations: selectedWorkLocations,
    onChange: onWorkLocationChange,
    workLocationOptions,
    onInputChange: onWorkLocationInputChange,
    isLoading: isWorkLocationLoading,
  } = useWorkLocationAutocomplete({ multiple: true });

  const {
    customerOrganizations: selectedCustomerOrgs,
    onChange: onCustomerOrgChange,
    customerOptions,
    onInputChange: onCustomerInputChange,
    isLoading: isCustomerLoading,
  } = useCustomerOrgCompanyAutocomplete({ multiple: true });

  const onSubmit = (values: OrderFilterFormTypes) => {
    const {
      queue,
      dateRange,
      orderHandler,
      statuses,
      workLocations,
      types,
      urgency,
      salesPersons,
      hasJobAd,
      isBillable,
      timeOrderedRange,
      customerOrg,
    } = values;

    const {
      displayValue: workLocationsDisplay,
      tooltip: workLocationsTooltip,
    } = makeMultiSelectDisplay(workLocations);
    const { displayValue: handlerDisplay, tooltip: handlerTooltip } =
      makeMultiSelectDisplay(orderHandler);
    const { displayValue: statusesDisplay, tooltip: statusesTooltip } =
      makeMultiSelectDisplay(statuses);
    const { displayValue: queueDisplay, tooltip: queueTooltip } =
      makeMultiSelectDisplay(queue);
    const { displayValue: typesDisplay, tooltip: typesTooltip } =
      makeMultiSelectDisplay(types);
    const { displayValue: salesPersonDisplay, tooltip: salesPersonTooltip } =
      makeMultiSelectDisplay(salesPersons);
    const { displayValue: customerOrgDisplay, tooltip: customerOrgTooltip } =
      makeMultiSelectDisplay(customerOrg);

    const newFilters: SearchFilter[] = [
      {
        name: "queue",
        value: queue.map((q) => q.id),
        displayValue: queueDisplay,
        ...(queueTooltip && { tooltip: queueTooltip }),
        lookupValues: makeLookupValues(queue),
      },
      {
        name: "dateFrom",
        value: dateToFormatOrEmpty(dateRange[0], isoFormat),
        type: "date",
      },
      {
        name: "dateTo",
        value: dateToFormatOrEmpty(dateRange[1], isoFormat),
        type: "date",
      },
      {
        name: "orderHandler",
        value: orderHandler.map((h) => h.id),
        displayValue: handlerDisplay,
        ...(handlerTooltip && { tooltip: handlerTooltip }),
        lookupValues: makeLookupValues(orderHandler),
      },
      {
        name: "statuses",
        value: statuses.map((s) => s.id),
        displayValue: statusesDisplay,
        ...(statusesTooltip && { tooltip: statusesTooltip }),
        lookupValues: makeLookupValues(statuses),
      },
      {
        name: "workLocations",
        value: workLocations.map((wl) => wl.id),
        displayValue: workLocationsDisplay,
        ...(workLocationsTooltip && { tooltip: workLocationsTooltip }),
        lookupValues: makeLookupValues(workLocations),
      },
      {
        name: "types",
        value: types.map((t) => t.id),
        displayValue: typesDisplay,
        ...(typesTooltip && { tooltip: typesTooltip }),
        lookupValues: makeLookupValues(types),
      },
      { name: "urgency", value: urgency },
      {
        name: "salesPersons",
        value: salesPersons.map((sp) => sp.id),
        displayValue: salesPersonDisplay,
        ...(salesPersonTooltip && { tooltip: salesPersonTooltip }),
        lookupValues: makeLookupValues(salesPersons),
      },
      {
        name: "hasJobAd",
        value: hasJobAd.toString(),
        displayValue: convertBooleanToYesNoValue(hasJobAd.toString()),
      },
      {
        name: "isBillable",
        displayName: "order:notBilled",
        value: isBillable !== "" ? (!isBillable).toString() : "",
        displayValue: convertBooleanToYesNoValue(isBillable.toString()),
      },
      {
        name: "timeOrderedFrom",
        value: dateToTimeOrEmpty(timeOrderedRange[0]),
        displayValue: dateToFormatOrEmpty(timeOrderedRange[0], dateTimeFormat),
        type: "text",
      },
      {
        name: "timeOrderedTo",
        value: dateToTimeOrEmpty(timeOrderedRange[1]),
        displayValue: dateToFormatOrEmpty(timeOrderedRange[1], dateTimeFormat),
        type: "text",
      },
      {
        name: "customerOrg",
        displayName: "customer",
        value: values.customerOrg.map((co) => co.id),
        displayValue: customerOrgDisplay,
        ...(customerOrgTooltip && { tooltip: customerOrgTooltip }),
        lookupValues: makeLookupValues(values.customerOrg),
      },
    ];

    handleMenuToggle();
    updateFilters(newFilters);
  };

  useEffect(() => {
    dispatch(fetchExpertUsers({}));
    dispatch(fetchOrderQueues()).then((action) => {
      if (isFulfilled(action)) {
        setOrderQueueOptions(
          action.payload.map((queue) => ({
            id: queue.id,
            name: queue.name,
          })),
        );
      }
    });
  }, [dispatch]);

  return (
    <Box sx={{ marginTop: "20px" }}>
      <Grid container spacing={0}>
        <Grid item xs={6}>
          <FilterRow label={t("date")}>
            <Controller
              name="dateRange"
              control={control}
              render={({ field }) => (
                <DateRangePicker
                  format={leadingZerosDateFormat}
                  value={field.value}
                  onChange={(newValue) => {
                    if (newValue[0] && newValue[1]) {
                      const start = new Date(newValue[0].setHours(0, 0, 0, 0));
                      const end = new Date(newValue[1].setHours(0, 0, 0, 0));
                      field.onChange([start, end]);
                    } else {
                      field.onChange(newValue);
                    }
                  }}
                  slotProps={{ textField: { size: "small", label: null } }}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("team")}>
            <Controller
              name="queue"
              control={control}
              render={({ field }) => (
                <SelectAutocompleteMulti
                  value={field.value}
                  onChange={(event) => {
                    field.onChange(event);
                  }}
                  options={[
                    {
                      id: FILTER_QUEUE_OWN.value,
                      name: t(FILTER_QUEUE_OWN.translationKey),
                    },
                    ...orderQueueOptions.sort((a, b) =>
                      a.name.localeCompare(b.name),
                    ),
                  ]}
                  translationNamespaces={["order"]}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("handler")}>
            <Controller
              name="orderHandler"
              control={control}
              render={({ field }) => (
                <SelectAutocompleteMulti
                  value={field.value}
                  onChange={(event) => {
                    field.onChange(event || []);
                  }}
                  options={[
                    { id: "noHandler", name: t("order:noHandler") },
                    { id: "autoHandler", name: t("order:automatching") },
                    ...(user ? [{ id: user.id, name: t("common:me") }] : []),
                    ...expertOptions.sort((a, b) =>
                      a.name.localeCompare(b.name),
                    ),
                  ]}
                  translationNamespaces={["order"]}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("orderStatus")}>
            <Controller
              name="statuses"
              control={control}
              render={({ field }) => {
                return (
                  <SelectAutocompleteMulti
                    value={field.value}
                    onChange={(event) => {
                      field.onChange(event);
                    }}
                    options={FILTER_ORDER_STATUS.map((status) => ({
                      id: status.value,
                      name: t(status.translationKey),
                    }))}
                    translationNamespaces={["order"]}
                  />
                );
              }}
            />
          </FilterRow>
          <FilterRow label={t("customer")}>
            <Controller
              name="customerOrg"
              control={control}
              render={({ field }) => {
                const handleCustomerOrgUpdate = (
                  newOrgs: CustomerOrganization[] | null,
                ) => {
                  onCustomerOrgChange(newOrgs || []);
                  field.onChange(
                    newOrgs
                      ? newOrgs.map((org) => ({ id: org.id, name: org.name }))
                      : [],
                  );
                };

                return (
                  <CustomerOrgCompanyAutocompleteMulti
                    options={customerOptions}
                    value={field.value || selectedCustomerOrgs || null}
                    onChange={handleCustomerOrgUpdate}
                    onInputChange={onCustomerInputChange}
                    isLoading={isCustomerLoading}
                    sx={{ width: "100%" }}
                    disableSalesPermissions
                  />
                );
              }}
            />
          </FilterRow>
          <FilterRow label={t("workLocation")}>
            <Controller
              name="workLocations"
              control={control}
              render={({ field }) => (
                <WorkLocationAutocomplete
                  multiple
                  options={workLocationOptions}
                  value={
                    (field.value as WorkLocation[]) || selectedWorkLocations
                  }
                  onChange={(newValue) => {
                    onWorkLocationChange(newValue);
                    field.onChange(
                      newValue
                        ? newValue.map((wl) => ({ id: wl.id, name: wl.name }))
                        : [],
                    );
                  }}
                  onInputChange={(value, reason) =>
                    onWorkLocationInputChange(value, reason)
                  }
                  isLoading={isWorkLocationLoading}
                  limitTags={2}
                />
              )}
            />
          </FilterRow>
          <CenteredGridItem item container spacing={2} xs={12}>
            <Grid item xs={12}>
              <RoundedButton
                variant="contained"
                onClick={handleSubmit(onSubmit)}
              >
                {t("getOrders")}
              </RoundedButton>
            </Grid>
          </CenteredGridItem>
        </Grid>
        <Grid item xs={6}>
          <FilterRow label={t("orderType")}>
            <Controller
              name="types"
              control={control}
              render={({ field }) => (
                <SelectAutocompleteMulti
                  value={field.value}
                  onChange={(event) => {
                    field.onChange(event);
                  }}
                  options={FILTER_ORDER_TYPES.map((status) => ({
                    id: status.value,
                    name: t(status.translationKey),
                  }))}
                  translationNamespaces={["order"]}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("urgency")}>
            <Controller
              name="urgency"
              control={control}
              render={({ field }) => (
                <SelectAutocompleteSingle
                  value={
                    FILTER_ORDER_URGENCY.find(
                      (opt) => opt.id === field.value,
                    ) || null
                  }
                  options={FILTER_ORDER_URGENCY}
                  onChange={(value) => {
                    field.onChange(value ? value.id : "");
                  }}
                  translationNamespaces={["order"]}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("salesPersons")}>
            <Controller
              name="salesPersons"
              control={control}
              render={({ field }) => (
                <SelectAutocompleteMulti
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  options={[
                    { id: user?.id ?? "", name: t("common:me") },
                    ...expertOptions.sort((a, b) =>
                      a.name.localeCompare(b.name),
                    ),
                  ]}
                  translationNamespaces={["order"]}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("jobAds:jobAd")}>
            <Controller
              name="hasJobAd"
              control={control}
              render={({ field }) => (
                <SelectAutocompleteSingle
                  value={
                    trueFalseAutocompleteOptions.find(
                      (opt) => opt.id === field.value?.toString(),
                    ) || null
                  }
                  options={trueFalseAutocompleteOptions}
                  onChange={(value) => {
                    field.onChange(value ? Boolean(value.id === "true") : "");
                  }}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("order:notBilled")}>
            <Controller
              name="isBillable"
              control={control}
              render={({ field }) => (
                <SelectAutocompleteSingle
                  value={
                    trueFalseAutocompleteOptions.find(
                      (opt) => opt.id === field.value?.toString(),
                    ) || null
                  }
                  options={trueFalseAutocompleteOptions}
                  onChange={(value) => {
                    field.onChange(value ? Boolean(value.id === "true") : "");
                  }}
                />
              )}
            />
          </FilterRow>
          <FilterRow label={t("timeOrdered")}>
            <Controller
              name="timeOrderedRange"
              control={control}
              render={({ field }) => (
                <DateTimeRangePicker
                  format={dateTimeFormat}
                  value={field.value}
                  onChange={(newValue) => field.onChange(newValue)}
                  slotProps={{ textField: { size: "small", label: null } }}
                />
              )}
            />
          </FilterRow>
        </Grid>
      </Grid>
    </Box>
  );
};
